import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AuthShell } from "./AuthShell";
import { GoogleButton } from "./GoogleButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Signup() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data.user);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("signup_title")} subtitle="Join the INVIXA trading ecosystem">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div>
          <Label className="text-slate-300">{t("name")}</Label>
          <Input required value={form.name} onChange={set("name")} data-testid="signup-name" className="mt-1.5 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-slate-300">{t("email")}</Label>
          <Input type="email" required value={form.email} onChange={set("email")} data-testid="signup-email" className="mt-1.5 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-slate-300">{t("password")}</Label>
          <Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password" className="mt-1.5 bg-white/5 border-white/10" placeholder="Min 6 characters" />
        </div>
        <div>
          <Label className="text-slate-300">{t("referral_optional")}</Label>
          <Input value={form.referral_code} onChange={set("referral_code")} data-testid="signup-referral" className="mt-1.5 bg-white/5 border-white/10" placeholder="INVX..." />
        </div>
        <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full gradient-brand text-brand-navy font-semibold hover:opacity-90">
          {loading ? t("loading") : t("nav_signup")}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-600"><div className="h-px flex-1 bg-white/10" />OR<div className="h-px flex-1 bg-white/10" /></div>
      <GoogleButton label={t("login_google")} />
      <p className="mt-6 text-center text-sm text-slate-400">
        {t("have_account")} <Link to="/login" className="text-brand-cyan hover:underline font-medium" data-testid="to-login">{t("nav_login")}</Link>
      </p>
    </AuthShell>
  );
}
